import { useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MainDialogLayout from '@/components/ui/MainDialogLayout/MainDialogLayout';
import { ManagementSupportedDtoIn } from '@/models/in/ManagementSupportedDtoIn';
import { SupportedUpdateDto } from '@/models/in/SupportedUpdateDto';
import { SupportedIsraeliIdChangeImpactDtoIn } from '@/models/in/SupportedIsraeliIdChangeImpactDtoIn';
import { usePutAuthApi } from '@/hooks/UseApiCalls';
import { getAuth } from '@/api/ApiCalls';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import { isValidIsraeliID } from '@/helpers/israeliIdValidator';
import Button from '@/components/ui/Button';
import IsraeliIdChangeImpact from '../IsraeliIdChangeImpact/IsraeliIdChangeImpact';
import { israeliIdChangeBlocker } from '../IsraeliIdChangeImpact/israeliIdChangeBlocker';
import IsraeliIdChangeBlockerMessage from '../IsraeliIdChangeImpact/IsraeliIdChangeBlockerMessage';
import styles from './SupportedEditDialog.module.css';
import { isHebrewName, toHebrewName } from '@/helpers/hebrewName';

interface SupportedEditDialogProps {
  open: boolean;
  onClose: () => void;
  supported: ManagementSupportedDtoIn;
  onSuccess: () => void;
  /** Set when a coordinator's request to correct this supported's ID waits: opens it for review. */
  onReviewPendingRequest?: () => void;
}

// Which field the current error belongs to, so it can be highlighted
type ErrorState = { field: 'israeliId' | 'firstName' | 'lastName' | 'general'; message: ReactNode };

const normalizeIsraeliId = (israeliId: string) => israeliId.trim().padStart(9, '0');

// The server's reason when it refuses, not axios's "Request failed with status code 409": a plain
// string from the service, or the first message of a model-validation 400 (e.g. [HebrewName])
const serverMessageOf = (err: unknown) => {
  if (!axios.isAxiosError(err)) return '';
  const data: unknown = err.response?.data;
  if (typeof data === 'string') return data.trim();
  const errors = (data as { errors?: Record<string, unknown> } | undefined)?.errors;
  const first = errors ? Object.values(errors)[0] : undefined;
  return Array.isArray(first) && typeof first[0] === 'string' ? first[0] : '';
};

const SupportedEditDialog = ({
  open,
  onClose,
  supported,
  onSuccess,
  onReviewPendingRequest,
}: SupportedEditDialogProps) => {
  const { t } = useTranslation();
  const { currentUser } = useAuthContext();
  const [israeliId, setIsraeliId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<ErrorState | null>(null);
  // Set once an ID change was checked: the dialog then shows what it touches and asks to confirm
  const [impact, setImpact] = useState<SupportedIsraeliIdChangeImpactDtoIn | null>(null);
  const [isLoadingImpact, setIsLoadingImpact] = useState(false);

  const { mutate: updateSupported, isPending: isSubmitting } = usePutAuthApi<void, object, SupportedUpdateDto>(
    `management/supporteds/${supported.id}`
  );

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      setIsraeliId(supported.israeliId || '');
      setFirstName(supported.firstName || '');
      setLastName(supported.lastName || '');
      setError(null);
      setImpact(null);
    }
  }, [open, supported]);

  const supportedName = `${supported.firstName} ${supported.lastName}`.trim();

  const isIsraeliIdChanged = israeliId.trim() !== ''
    && normalizeIsraeliId(israeliId) !== normalizeIsraeliId(supported.israeliId || '');

  const save = () => {
    const body: SupportedUpdateDto = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      // Unchanged goes back as it is stored, so a legacy ID the check digit would reject still saves the name
      israeliId: isIsraeliIdChanged ? normalizeIsraeliId(israeliId) : supported.israeliId,
    };

    updateSupported(
      { body },
      {
        onSuccess: () => {
          onSuccess();
          handleClose();
        },
        onError: (err) => {
          // The server says why an ID cannot be given and what to do instead
          const serverMessage = serverMessageOf(err);
          setImpact(null);
          setError({
            field: isIsraeliIdChanged && serverMessage ? 'israeliId' : 'general',
            message: serverMessage
              || t('supportedsManagement.editSupportedDialog.errorUpdating') + ': ' + (err as Error).message,
          });
        },
      }
    );
  };

  // The ID belongs to the person, not to one fund: before it changes, the office sees every
  // place the supported is in and what moves along (Druk, HAT-1428)
  const reviewIsraeliIdChange = async () => {
    setIsLoadingImpact(true);
    try {
      const newIsraeliId = normalizeIsraeliId(israeliId);
      const result = await getAuth<SupportedIsraeliIdChangeImpactDtoIn>(
        `management/supporteds/${supported.id}/israeli-id-change-impact?newIsraeliId=${newIsraeliId}`,
        { Authorization: `Bearer ${currentUser?.token}` }
      );
      // A change the server would refuse is said under the field, with no confirmation to click through
      const blocker = israeliIdChangeBlocker(result);
      if (blocker) {
        setError({
          field: 'israeliId',
          message: (
            <IsraeliIdChangeBlockerMessage
              blocker={blocker}
              contactId={supported.id}
              supportedName={supportedName}
              newIsraeliId={newIsraeliId}
            />
          ),
        });
        return;
      }
      setImpact(result);
    } catch (err) {
      setError({
        field: 'general',
        message: serverMessageOf(err) || t('supportedsManagement.editSupportedDialog.errorLoadingImpact'),
      });
    } finally {
      setIsLoadingImpact(false);
    }
  };

  const handleSubmit = () => {
    if (!israeliId.trim()) {
      setError({ field: 'israeliId', message: t('supportedsManagement.editSupportedDialog.israeliIdRequired') });
      return;
    }

    if (isIsraeliIdChanged && !isValidIsraeliID(israeliId)) {
      setError({ field: 'israeliId', message: t('supportedsManagement.editSupportedDialog.invalidIsraeliId') });
      return;
    }

    if (!firstName.trim()) {
      setError({ field: 'firstName', message: t('supportedsManagement.editSupportedDialog.firstNameRequired') });
      return;
    }

    // Hebrew letters only for a name that changes (SPR-1463) - one saved before the rule stays as
    // it is, so it does not block a change of the ID alone
    if (firstName.trim() !== (supported.firstName ?? '').trim() && !isHebrewName(firstName.trim())) {
      setError({ field: 'firstName', message: t('hebrewNameOnly') });
      return;
    }

    if (!lastName.trim()) {
      setError({ field: 'lastName', message: t('supportedsManagement.editSupportedDialog.lastNameRequired') });
      return;
    }

    if (lastName.trim() !== (supported.lastName ?? '').trim() && !isHebrewName(lastName.trim())) {
      setError({ field: 'lastName', message: t('hebrewNameOnly') });
      return;
    }

    setError(null);

    if (isIsraeliIdChanged) {
      void reviewIsraeliIdChange();
    } else {
      save();
    }
  };

  const handleClose = () => {
    setIsraeliId('');
    setFirstName('');
    setLastName('');
    setError(null);
    setImpact(null);
    onClose();
  };

  const inputClassName = (field: ErrorState['field']) =>
    `${styles.input} ${error?.field === field ? styles.hasError : ''}`;

  const fieldError = (field: ErrorState['field']) =>
    error?.field === field && !impact ? <div className={styles.error} role="alert">{error.message}</div> : null;

  return (
    <MainDialogLayout
      open={open}
      onOpenChange={(isOpen: boolean) => {
        if (!isOpen) {
          handleClose();
        }
      }}
      title={t(impact
        ? 'supportedsManagement.editSupportedDialog.impact.title'
        : 'supportedsManagement.editSupportedDialog.title')}
    >
      <div className={styles.content}>
        {impact ? (
          <IsraeliIdChangeImpact
            impact={impact}
            contactId={supported.id}
            supportedName={supportedName}
            currentIsraeliId={supported.israeliId}
            newIsraeliId={normalizeIsraeliId(israeliId)}
            onOpenPendingRequest={onReviewPendingRequest}
          />
        ) : (
          <>
            <div className={styles.inputSection}>
              <label htmlFor="israeliId" className={styles.inputLabel}>
                {t('supportedsManagement.editSupportedDialog.israeliId')}
              </label>
              <input
                id="israeliId"
                type="text"
                inputMode="numeric"
                maxLength={9}
                className={`${inputClassName('israeliId')} ${styles.ltrInput}`}
                value={israeliId}
                onChange={(e) => {
                  setIsraeliId(e.target.value.replace(/\D/g, ''));
                  if (error) setError(null);
                }}
                dir="ltr"
              />
              {fieldError('israeliId')}
            </div>

            <div className={styles.inputSection}>
              <label htmlFor="firstName" className={styles.inputLabel}>
                {t('supportedsManagement.editSupportedDialog.firstName')}
              </label>
              <input
                id="firstName"
                type="text"
                className={inputClassName('firstName')}
                value={firstName}
                onChange={(e) => {
                  setFirstName(toHebrewName(e.target.value));
                  if (error) setError(null);
                }}
                dir="rtl"
              />
              {fieldError('firstName')}
            </div>

            <div className={styles.inputSection}>
              <label htmlFor="lastName" className={styles.inputLabel}>
                {t('supportedsManagement.editSupportedDialog.lastName')}
              </label>
              <input
                id="lastName"
                type="text"
                className={inputClassName('lastName')}
                value={lastName}
                onChange={(e) => {
                  setLastName(toHebrewName(e.target.value));
                  if (error) setError(null);
                }}
                dir="rtl"
              />
              {fieldError('lastName')}
            </div>
          </>
        )}

        {(error?.field === 'general' || (impact && error)) && (
          <p className={styles.error} role="alert">{error.message}</p>
        )}

        <div className={styles.actions}>
          {impact ? (
            <>
              <Button
                variant="white"
                className={styles.secondaryButton}
                onClick={() => setImpact(null)}
                disabled={isSubmitting}
              >
                {t('supportedsManagement.editSupportedDialog.impact.back')}
              </Button>
              <Button
                variant="primary"
                className={styles.actionButton}
                onClick={save}
                disabled={isSubmitting}
              >
                {isSubmitting ? t('saving') : t('supportedsManagement.editSupportedDialog.impact.confirm')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="white"
                className={styles.secondaryButton}
                onClick={handleClose}
                disabled={isSubmitting || isLoadingImpact}
              >
                {t('close')}
              </Button>
              <Button
                variant="primary"
                className={styles.actionButton}
                onClick={handleSubmit}
                disabled={isSubmitting || isLoadingImpact}
              >
                {isSubmitting || isLoadingImpact ? t('saving') : t('save')}
              </Button>
            </>
          )}
        </div>
      </div>
    </MainDialogLayout>
  );
};

export default SupportedEditDialog;
